import { useMemo, useState } from "react";
import { forumPosts } from "@/mocks/communityData";
import SectionTitle from "./SectionTitle";

const groupTabs = ["全部讨论", "娱乐八卦", "社会热议", "互联网圈", "职场瓜田", "数码前沿", "饭圈观察"];

export default function Forum() {
  const [tab, setTab] = useState("全部讨论");
  const [joined, setJoined] = useState<string[]>([]);

  const groups = useMemo(() => Array.from(new Set(forumPosts.map((p) => p.group))), []);

  const filtered = tab === "全部讨论" ? forumPosts : forumPosts.filter((p) => p.group === tab);

  const toggleJoin = (name: string) =>
    setJoined((prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]));

  return (
    <section id="forum" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionTitle
          eyebrow="群组互动"
          title="群组讨论区 · 大家一起来吃瓜"
          desc="进群开聊、盖楼发言，和同好一起把瓜吃明白。理性围观，快乐吃瓜。"
        />

        <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
          {/* left: posts */}
          <div>
            {/* group tabs */}
            <div className="mb-5 flex flex-wrap gap-2">
              {groupTabs.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setTab(g)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                    tab === g ? "bg-primary-500 text-background-50" : "bg-background-50 text-foreground-600 hover:bg-background-200/70"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {filtered.map((post) => (
                <article
                  key={post.id}
                  className="flex items-start gap-3 rounded-2xl border border-background-200 bg-background-50 p-4 transition hover:border-primary-200"
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-background-50 ${post.avatarColor}`}
                  >
                    {post.avatarText}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {post.hot && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                          <i className="ri-fire-fill text-xs" aria-hidden="true"></i>
                          热帖
                        </span>
                      )}
                      <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-700">
                        {post.group}
                      </span>
                      <span className="text-xs text-foreground-400">{post.time}</span>
                    </div>
                    <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950">
                      <a href="#rank" className="transition hover:text-primary-600">
                        {post.title}
                      </a>
                    </h3>
                    <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-3-line text-sm" aria-hidden="true"></i>
                        {post.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-1-line text-sm" aria-hidden="true"></i>
                        {post.replies} 回复
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-thumb-up-line text-sm" aria-hidden="true"></i>
                        {post.likes}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleJoin(post.group)}
                    className={`mt-1 hidden shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition sm:inline-flex ${
                      joined.includes(post.group)
                        ? "bg-accent-500 text-background-50"
                        : "border border-primary-200 text-primary-700 hover:bg-primary-100/70"
                    }`}
                  >
                    {joined.includes(post.group) ? "已入群" : "入群围观"}
                  </button>
                </article>
              ))}
            </div>

            {filtered.length === 0 && (
              <p className="py-14 text-center text-sm text-foreground-500">该群组暂无最新讨论，换个群组看看。</p>
            )}
          </div>

          {/* right: join panel */}
          <aside className="space-y-4">
            <div className="rounded-2xl border border-background-200 bg-gradient-to-b from-primary-50 to-background-50 p-5">
              <h4 className="font-heading text-base font-bold text-foreground-950">
                <i className="ri-group-2-fill mr-1 text-primary-500" aria-hidden="true"></i>
                找到组织，开吃！
              </h4>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                加入你最爱的群组，第一时间收到圈内热议，和同好一起盖楼。目前已开放 {groups.length} 个群组的围观入口。
              </p>
              <a
                href="#newsletter"
                className="mt-4 flex items-center justify-center gap-1 whitespace-nowrap rounded-full bg-primary-500 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
              >
                订阅吃瓜日报
                <i className="ri-mail-send-line" aria-hidden="true"></i>
              </a>
            </div>
            <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
              <h4 className="font-heading text-base font-bold text-foreground-950">社区公约</h4>
              <ul className="mt-3 space-y-2 text-sm text-foreground-600">
                {["理性围观，不传谣不造谣", "友善交流，尊重不同声音", "来源标注，让讨论更可信"].map((rule) => (
                  <li key={rule} className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-accent-500" aria-hidden="true"></i>
                    {rule}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}